import { isEmpty } from '@/utils';
import { createRoot } from 'react-dom/client';
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useState,
  useRef
} from 'react';
import './index.scss';
export interface BtnItem {
  type: 'primary' | 'default';
  btnText: string;
  btnStyle?: CSSProperties;
  onClick?: (cb: any) => void;
  autoClose?: boolean; // showModal时使用
  primaryColor?: string;
  defaultColor?: string;
  disabled?: boolean;
}
export interface ModalProps {
  title?: ReactNode;
  content?: ReactNode;
  footer?: BtnItem[];
  visible?: boolean;
  closeModal?: () => void;
  children?: ReactNode;
  className?: string;
  showCloseBtn?: boolean;
}
// 使用传统函数声明代替箭头函数，兼容ES2015
function Modal(props: ModalProps) {
  // 使用传统方式解构对象，避免使用解构赋值语法
  const title = props.title;
  const content = props.content;
  const footer = props.footer;
  const visible = props.visible;
  const closeModal = props.closeModal;
  const children = props.children;
  const className = props.className;
  const showCloseBtn = props.showCloseBtn;
  const [modalVisible, setModalVisible] = useState(!!visible);
  const historyStateRef = useRef<string | null>(null);

  // 使用传统函数声明代替箭头函数，兼容ES2015
  useEffect(
    function () {
      setModalVisible(!!visible);
    },
    [visible]
  );

  // 使用传统函数声明代替箭头函数，兼容ES2015
  useEffect(
    function () {
      if (modalVisible) {
        document.body.classList.add('body-overflow-hidden');
      } else {
        document.body.classList.remove('body-overflow-hidden');
      }

      // 使用传统函数声明代替箭头函数，兼容ES2015
      return function () {
        document.body.classList.remove('body-overflow-hidden');
      };
    },
    [modalVisible]
  );

  // 处理Android返回键/手势的逻辑
  useEffect(
    function () {
      if (modalVisible) {
        // 当弹窗显示时，向历史记录中添加一个状态
        const modalStateId = 'modal_' + Date.now();
        historyStateRef.current = modalStateId;

        // 监听popstate事件（用户点击返回键或使用手势返回）
        const handlePopState = function (event: PopStateEvent) {
          // 检查是否是我们的弹窗状态被移除
          if (historyStateRef.current) {
            // 用户执行了返回操作，关闭弹窗
            if (closeModal) {
              closeModal();
            }
            setModalVisible(false);
            historyStateRef.current = null;
          }
        };

        window.addEventListener('popstate', handlePopState);

        // 清理函数
        return function () {
          window.removeEventListener('popstate', handlePopState);
          historyStateRef.current = null;
        };
      }
    },
    [modalVisible, closeModal]
  );

  if (!modalVisible) {
    return null;
  }

  /**
   * 渲染模态框底部按钮组。
   * @param footer - 按钮组数据。
   * @returns - 如果footer不为空，则返回渲染的按钮组；否则返回null。
   */
  // 使用传统函数声明代替箭头函数，兼容ES2015
  const renderFooter = function () {
    if (!isEmpty(footer)) {
      return (
        <div className="modal-btn-group">
          {/* 使用条件检查代替可选链操作符 */}
          {footer &&
            footer.map(function (item: BtnItem, index: number) {
              const btnStyle: CSSProperties = {
                // 使用条件运算符代替空值合并运算符
                color:
                  item.type === 'primary'
                    ? item.primaryColor
                      ? item.primaryColor
                      : '#3C6EF0'
                    : item.defaultColor
                    ? item.defaultColor
                    : '#868D9F',
                fontSize: '16px',
                fontFamily: 'PingFang SC'
              };

              // 使用Object.assign代替展开运算符
              if (item.btnStyle) {
                Object.assign(btnStyle, item.btnStyle);
              }

              return (
                <a
                  key={index}
                  style={btnStyle}
                  className={
                    'btn-' + item.type + (item.disabled ? ' disabled' : '')
                  }
                  onClick={function (e: React.MouseEvent<HTMLAnchorElement>) {
                    if (item.disabled) {
                      return;
                    }
                    const target = e.target;
                    // 使用传统函数声明代替箭头函数，兼容ES2015
                    const removeModalDom = function () {
                      // 使用条件检查代替可选链操作符
                      const targetElement = target as HTMLElement;
                      const parentNode =
                        targetElement && targetElement.closest
                          ? targetElement.closest('.modal-container')
                          : null;

                      if (parentNode) {
                        document.body.removeChild(parentNode);
                      }
                      if (closeModal) {
                        closeModal();
                      }
                      setModalVisible(false);
                      historyStateRef.current = null;
                    };

                    if (item.onClick) {
                      item.onClick(removeModalDom);
                    }

                    if (item.autoClose) {
                      removeModalDom();
                    }
                  }}
                >
                  {item.btnText}
                </a>
              );
            })}
        </div>
      );
    }
    return null;
  };
  return (
    <div
      className={`modal-mask ${className || ''}`}
      data-qidian-cid="h5_modal"
      clstag="h5-modal-imp"
    >
      <div className="modal-content">
        {title && <div className="modal-content_title">{title}</div>}
        {showCloseBtn && (
          <div
            className="modal-close-btn"
            onClick={(e) => {
              const target = e.target;
              // 使用传统函数声明代替箭头函数，兼容ES2015
              const removeModalDom = function () {
                // 使用条件检查代替可选链操作符
                const targetElement = target as HTMLElement;
                const parentNode =
                  targetElement && targetElement.closest
                    ? targetElement.closest('.modal-container')
                    : null;

                if (parentNode) {
                  document.body.removeChild(parentNode);
                }
                if (closeModal) {
                  closeModal();
                }
                setModalVisible(false);
                historyStateRef.current = null;
              };
              removeModalDom();
            }}
          >
            X
          </div>
        )}
        {(content || children) && (
          <div className="content-info">{content || children}</div>
        )}
        {renderFooter()}
      </div>
    </div>
  );
}

/**
 * 显示模态框
 * @param props - 模态框属性
 */
// 使用传统函数声明代替箭头函数，兼容ES2015
export function showModal(props: ModalProps) {
  const div = document.createElement('div');
  div.className = 'modal-container';
  document.body.appendChild(div);
  const root = createRoot(div);

  // 使用传统函数声明代替箭头函数，兼容ES2015
  const unmountModal = function () {
    // 确保在卸载时移除DOM节点
    if (div && div.parentNode) {
      div.parentNode.removeChild(div);
    }
    root.unmount();
  };

  // 使用Object.assign代替展开运算符
  const modalProps = Object.assign({}, props, {
    visible: true,
    closeModal: unmountModal
  });

  root.render(
    <Modal
      title={modalProps.title}
      content={modalProps.content}
      footer={modalProps.footer}
      visible={modalProps.visible}
      closeModal={modalProps.closeModal}
      children={modalProps.children}
      className={modalProps.className}
      showCloseBtn={modalProps.showCloseBtn}
    />
  );
}
export default Modal;
